import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getTranslations } from '../utils/translations';
import './Navbar.css';

export default function Navbar() {
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const t = getTranslations(language);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const scrollToSection = (sectionId) => {
    closeMenu();

    if (location.pathname.startsWith('/blog')) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    closeMenu();
    navigate('/');
  };

  const goBlog = (e) => {
    e.preventDefault();
    closeMenu();
    navigate('/blog');
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const navLinks = (
    <>
      <a href="/" onClick={goHome}>{t.nav.home}</a>
      <a href="/blog" onClick={goBlog}>{t.nav.blog}</a>
      <a href="#destinations" onClick={(e) => { e.preventDefault(); scrollToSection('destinations'); }}>{t.nav.explore}</a>
      <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>{t.nav.services}</a>
      <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>{t.nav.aboutUs}</a>
      <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>{t.nav.contact}</a>
    </>
  );

  return (
    <nav className="navbar">
      <div className="navbar-top-bar">
        <a
          href="https://wa.me/48730759753"
          target="_blank"
          rel="noopener noreferrer"
          className="navbar-top-contact"
          aria-label="Speak to an expert on WhatsApp"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          <span>Speak to an expert</span>
        </a>
        <div className="navbar-lang-switcher" role="group" aria-label="Language">
          <svg className="navbar-lang-globe" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          <button
            type="button"
            className={`navbar-lang-option${language === 'en' ? ' is-active' : ''}`}
            onClick={() => setLanguage('en')}
            aria-pressed={language === 'en'}
          >
            EN
          </button>
          <span className="navbar-lang-divider" aria-hidden="true">|</span>
          <button
            type="button"
            className={`navbar-lang-option${language === 'ar' ? ' is-active' : ''}`}
            onClick={() => setLanguage('ar')}
            aria-pressed={language === 'ar'}
          >
            AR
          </button>
        </div>
      </div>
      
      <div className="navbar-main">
        <div className="navbar-container">
          <div className="navbar-main-container">
            <div className="navbar-brand">
              <span className="navbar-brand-text">SEFARIUM | PREMIUM TRAVEL SERVICES</span>
            </div>

            <button
              type="button"
              className={`navbar-menu-toggle${menuOpen ? ' is-open' : ''}`}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="navbar-mobile-menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="navbar-menu-toggle-bar" />
              <span className="navbar-menu-toggle-bar" />
              <span className="navbar-menu-toggle-bar" />
            </button>
            
            <div className="navbar-content">
              <nav className="navbar-nav navbar-nav-desktop" aria-label="Main">
                {navLinks}
              </nav>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`navbar-mobile-backdrop${menuOpen ? ' is-open' : ''}`}
        onClick={closeMenu}
        aria-hidden={!menuOpen}
      />

      <nav
        id="navbar-mobile-menu"
        className={`navbar-nav navbar-nav-mobile${menuOpen ? ' is-open' : ''}`}
        aria-label="Mobile"
        aria-hidden={!menuOpen}
      >
        {navLinks}
      </nav>
    </nav>
  );
}
